import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiGet } from '../utils/api';
import './StudentDetail.css';
import { 
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Cake as CakeIcon,
  School as SchoolIcon,
  MenuBook as MenuBookIcon,
  Group as GroupIcon,
  Class as ClassIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import Sidebar from './Sidebar';
import Header from './Header';

interface Student {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  date_of_birth: string | null;
  age: number | null;
  reading_level: number | null;
  grade_level: string | null;
  assignment_count: number;
  cultures: string[];
  email: string | null;
  student_id: string | null;
  parent_email: string | null;
  phone: string | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  last_activity_date: string | null;
  class_ids: number[];
  class_count: number;
  classes?: Class[];
}

interface Class {
  id: number;
  name: string;
  subject: string | null;
  grade_level: string | null;
  description: string | null;
  room_number: string | null;
  period: string | null;
  start_time: string | null;
  end_time: string | null;
  days_of_week: string | null;
  academic_year: string | null;
  semester: string | null;
  teacher_id: number;
  student_count: number;
  is_active: boolean;
  is_archived: boolean;
}

const StudentDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: authLoading } = useAuth();
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch student data
  const fetchStudent = async () => {
    if (!token || !isAuthenticated || !id) return;
    
    try {
      setLoading(true);
      const response = await apiGet(`/api/students/${id}?include_classes=true`, token);

      if (response.ok) {
        const data = await response.json();
        setStudent(data.student);
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('studentDetail.errors.failedToLoad'));
      }
    } catch (err) {
      console.error('Error fetching student:', err);
      setError(t('studentDetail.errors.networkError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated && token) {
      fetchStudent();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, token, id]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatTime = (timeString: string | null) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const handleEdit = () => {
    navigate(`/students?edit=${id}`);
  };

  const handleBack = () => {
    navigate('/students');
  };

  // Authentication check
  if (authLoading) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="loading-state">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="error-state">
              <p>Please log in to access student details.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="loading-state">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading student details...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="error-state">
              <p>Error: {error}</p>
              <div className="error-actions">
                <button onClick={handleBack} className="btn btn-secondary">
                  <ArrowBackIcon />
                  Back to Students
                </button>
                <button onClick={fetchStudent} className="btn btn-primary">
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Student not found
  if (!student) {
    return (
      <div className="dashboard">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="content-area">
            <div className="error-state">
              <h2>Student Not Found</h2>
              <p>The student you're looking for doesn't exist or you don't have permission to view them.</p>
              <button onClick={handleBack} className="btn btn-primary">
                <ArrowBackIcon />
                Back to Students
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main student detail view
  return (
    <div className="dashboard">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="content-area">
          <div className="student-detail">
            {/* Header Section */}
            <div className="student-detail-header">
              <div className="header-navigation">
                <button onClick={handleBack} className="back-button">
                  <ArrowBackIcon />
                  Back to Students
                </button>
              </div>
              
              <div className="student-header-card">
                <div className="student-avatar-large">
                  <PersonIcon />
                </div>
                <div className="student-header-info">
                  <h1>{student.full_name}</h1>
                  <div className="student-badges">
                    {student.grade_level && (
                      <span className="badge badge-grade">{student.grade_level}</span>
                    )}
                    {student.reading_level && (
                      <span className="badge badge-reading">Level {student.reading_level}</span>
                    )}
                    {student.age && (
                      <span className="badge badge-age">Age {student.age}</span>
                    )}
                  </div>
                  <div className="student-summary">
                    <span className="summary-item">
                      <ClassIcon />
                      {student.class_count} {student.class_count === 1 ? 'Class' : 'Classes'}
                    </span>
                    <span className="summary-item">
                      <MenuBookIcon />
                      {student.assignment_count} Assignments
                    </span>
                  </div>
                </div>
                <div className="header-actions">
                  <button onClick={handleEdit} className="btn btn-primary">
                    <EditIcon />
                    Edit Student
                  </button>
                </div>
              </div>
            </div>

            {/* Content Grid */}
            <div className="student-detail-content">
              {/* Basic Information */}
              <div className="detail-section">
                <div className="section-header">
                  <PersonIcon />
                  <h2>Basic Information</h2>
                </div>
                <div className="section-content">
                  <div className="info-grid">
                    <div className="info-item">
                      <label>First Name</label>
                      <span>{student.first_name}</span>
                    </div>
                    <div className="info-item">
                      <label>Last Name</label>
                      <span>{student.last_name}</span>
                    </div>
                    <div className="info-item">
                      <label>Student ID</label>
                      <span>{student.student_id || 'Not assigned'}</span>
                    </div>
                    <div className="info-item">
                      <label>Date of Birth</label>
                      <div className="date-info">
                        <CakeIcon />
                        <span>{formatDate(student.date_of_birth)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Academic Information */}
              <div className="detail-section">
                <div className="section-header">
                  <SchoolIcon />
                  <h2>Academic Information</h2>
                </div>
                <div className="section-content">
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Grade Level</label>
                      <span>{student.grade_level || 'Not assigned'}</span>
                    </div>
                    <div className="info-item">
                      <label>{t('classWork.readingLevel')}</label>
                      <span>{student.reading_level ? `Level ${student.reading_level}` : 'Not assessed'}</span>
                    </div>
                    <div className="info-item">
                      <label>Assignment Count</label>
                      <span>{student.assignment_count}</span>
                    </div>
                    <div className="info-item">
                      <label>Last Activity</label>
                      <div className="date-info">
                        <CalendarIcon />
                        <span>{student.last_activity_date ? formatDate(student.last_activity_date) : 'No recent activity'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="detail-section">
                <div className="section-header">
                  <EmailIcon />
                  <h2>Contact Information</h2>
                </div>
                <div className="section-content">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div className="info-item">
                      <label>Student Email</label>
                      <div className="contact-info">
                        <EmailIcon />
                        <span style={{ wordBreak: 'break-all' }}>{student.email || 'Not provided'}</span>
                      </div>
                    </div>
                    <div className="info-item">
                      <label>Parent Email</label>
                      <div className="contact-info">
                        <EmailIcon />
                        <span style={{ wordBreak: 'break-all' }}>{student.parent_email || 'Not provided'}</span>
                      </div>
                    </div>
                    <div className="info-item">
                      <label>Phone</label>
                      <div className="contact-info">
                        <PhoneIcon />
                        <span>{student.phone || 'Not provided'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cultural Background */}
              <div className="detail-section">
                <div className="section-header">
                  <GroupIcon />
                  <h2>Cultural Background</h2>
                </div>
                <div className="section-content">
                  {student.cultures.length > 0 ? (
                    <div className="culture-tags">
                      {student.cultures.map((culture, index) => (
                        <span key={index} className="culture-tag">
                          {culture}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">No cultural backgrounds assigned</p>
                  )}
                </div>
              </div>

              {/* Classes Enrolled */}
              <div className="detail-section">
                <div className="section-header">
                  <ClassIcon />
                  <h2>Classes Enrolled ({student.class_count})</h2>
                </div>
                <div className="section-content">
                  {student.classes && student.classes.length > 0 ? (
                    <div className="student-classes-grid">
                      {student.classes.map((cls) => (
                        <div key={cls.id} className="class-card">
                          <div className="class-card-header">
                            <h3>{cls.name}</h3>
                            {cls.subject && (
                              <span className="class-subject">{cls.subject}</span>
                            )}
                          </div>
                          <div className="class-card-info">
                            {cls.room_number && (
                              <div className="class-info-item">
                                <label>Room:</label>
                                <span>{cls.room_number}</span>
                              </div>
                            )}
                            {cls.period && (
                              <div className="class-info-item">
                                <label>Period:</label>
                                <span>{cls.period}</span>
                              </div>
                            )}
                            {cls.start_time && cls.end_time && (
                              <div className="class-info-item">
                                <label>Time:</label>
                                <span>{formatTime(cls.start_time)} - {formatTime(cls.end_time)}</span>
                              </div>
                            )}
                            {cls.days_of_week && (
                              <div className="class-info-item">
                                <label>Days:</label>
                                <span>{cls.days_of_week}</span>
                              </div>
                            )}
                            <div className="class-info-item">
                              <label>Students:</label>
                              <span>{cls.student_count}</span>
                            </div>
                          </div>
                          {cls.description && (
                            <div className="class-description">
                              <p>{cls.description}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-data">
                      <ClassIcon />
                      <h3>Not enrolled in any classes</h3>
                      <p>This student is not currently enrolled in any classes.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes Section */}
              {student.notes && (
                <div className="detail-section">
                  <div className="section-header">
                    <MenuBookIcon />
                    <h2>{t('modals.teacherNotes')}</h2>
                  </div>
                  <div className="section-content">
                    <div className="notes-content">
                      <p>{student.notes}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDetail;
